import putFetcher from 'api/utils/putFetcher';
import { useCallback, useMemo } from 'react';
import type { HologramSimIdsMap } from 'types/common';
import type { ToggleTunnelingReturn } from 'types/legacyDashboard';

interface ToggleTunnelingProps {
  sims?: HologramSimIdsMap;
  organizationId?: OrgId;
  isSuperAdmin: boolean;
}

// TODO: Replace by bulk API call

// Max chunk is number of devices in a request, max requests is number of API calls.
// Right now this endpoint only supports 1 device per request. Written in such a way
// that we could expand later or remove entirely and use a bulk endpoint
const MAX_CHUNK = 1 as const;
const MAX_REQUESTS = 5 as const;

async function* resultGenerator(
  baseUrl: string,
  urlSearchParams: URLSearchParams,
  requestChunks: Array<Array<Array<DeviceId>>>,
  tunnelable: boolean
) {
  let i = 0;

  while (i < requestChunks.length) {
    const requests = requestChunks[i].map(async (chunk) => {
      const result: ToggleTunnelingReturn = await putFetcher<{ tunnelable: boolean }>({
        url: `${baseUrl}${chunk[0]}?${urlSearchParams}`,
        body: {
          tunnelable,
        },
        throwOnError: false,
      });

      return { ...result, deviceId: chunk[0] };
    });

    yield Promise.all(requests);
    i += 1;
  }
}

const useToggleTunneling = ({ sims, organizationId, isSuperAdmin }: ToggleTunnelingProps) => {
  const urlSearchParams = useMemo(
    () =>
      isSuperAdmin ? new URLSearchParams() : new URLSearchParams({ orgid: `${organizationId}` }),
    [isSuperAdmin, organizationId]
  );

  const baseUrl = `${process.env.VITE_API_ROOT}/devices/`;

  const requestChunks = useMemo(() => {
    const chunks = [...sims!.values()].reduce<Array<Array<DeviceId>>>(
      (resultArray, item, index) => {
        const chunkIndex = Math.floor(index / MAX_CHUNK);
        if (item) {
          if (!resultArray[chunkIndex]) {
            // eslint-disable-next-line no-param-reassign
            resultArray[chunkIndex] = [];
          }

          resultArray[chunkIndex].push(item);

          return resultArray;
        }
        return [];
      },
      []
    );

    return chunks.reduce<Array<Array<Array<DeviceId>>>>((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / MAX_REQUESTS);

      if (!resultArray[chunkIndex]) {
        // eslint-disable-next-line no-param-reassign
        resultArray[chunkIndex] = [];
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);
  }, [sims]);

  const callback = useCallback(
    async (tunnelable: boolean) => {
      let results: Array<ToggleTunnelingReturn & { deviceId: DeviceId }> = [];
      // eslint-disable-next-line no-restricted-syntax
      for await (const resultChunk of resultGenerator(
        baseUrl,
        urlSearchParams,
        requestChunks,
        tunnelable
      )) {
        results = results.concat(resultChunk);
      }

      return results;
    },
    [baseUrl, requestChunks, urlSearchParams]
  );

  return callback;
};

export default useToggleTunneling;
